import { FC, KeyboardEvent } from 'react';
import classNames from 'classnames';

import Checkbox from '@zvuk-b2b/react-uikit/ui/Checkbox';
import IconButton from '@zvuk-b2b/react-uikit/ui/IconButton';
import IconDownload from '@zvuk-b2b/react-uikit/ui/icons/Download';
import IconTrash from '@zvuk-b2b/react-uikit/ui/icons/Trash2';
import IconRefreshCcw from '@zvuk-b2b/react-uikit/ui/icons/RefreshCcw';

import { PromoRollSynthJobFieldsFragment, PromoRollSynthJobStatus } from '@entities/promo-roll-synth-job/model';
import { PromoRollSynthJobCard } from '@entities/promo-roll-synth-job';
import { PlayButton } from 'application/components/PlayButton';
import MediaListItemProgressSlider from 'application/components/MediaList/MediaListItem/MediaListItemProgressSlider';
import { usePlayerContext } from 'application/providers/PlayerProvider';
import FileUtils from 'application/utils/FileUtils';

import AnalyticsService, { EventNameAMP } from 'application/services/AnalyticsService';

import { usePromoRollSynthJobFailureMessage } from '../../model/usePromoRollSynthJobFailureMessage';
import { TEXTS } from '../PromoRollSynthModal/texts';
import styles from './PromoRollSynthJobItem.module.scss';

type Props = {
  job: PromoRollSynthJobFieldsFragment;
  isSelected?: boolean;
  isHighlighted?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
  onDelete?: (id: string) => void;
  onRetry?: (job: PromoRollSynthJobFieldsFragment) => void;
};

const PromoRollSynthJobItem: FC<Props> = ({
  job,
  isSelected = false,
  isHighlighted = false,
  onSelect,
  onDelete,
  onRetry,
}) => {
  const isDone = job.status === PromoRollSynthJobStatus.Completed;
  const isFailed = job.status === PromoRollSynthJobStatus.Failed;
  const isProcessing = job.status === PromoRollSynthJobStatus.InProgress;
  const isQueued = job.status === PromoRollSynthJobStatus.Created;

  const {
    play,
    pause,
    resume,
    stop,
    isPaused,
    fileUuid,
    isLoading: isTrackLoading,
    progress,
    changeProgress,
  } = usePlayerContext();

  const jobFileId = `job_promo_${job.id}`;
  const isThisPlaying = fileUuid === jobFileId;
  const isThisTrackLoading = isThisPlaying && isTrackLoading;

  const failureMessage = usePromoRollSynthJobFailureMessage(job.id);

  const mediafileUrl = job.promoMediafile ? FileUtils.getMediafileUrl(job.promoMediafile.sourcefiles) : '';

  const handlePlay = () => {
    if (!mediafileUrl) return;

    if (isThisPlaying && !isPaused) {
      pause();
    } else if (isThisPlaying && isPaused) {
      resume();
    } else {
      play({ fileUuid: jobFileId, fileName: job.promoMediafile!.filename, fileUrl: mediafileUrl });
    }
  };

  const handlePlayKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handlePlay();
    }
  };

  const handleDelete = () => {
    if (isThisPlaying) stop();
    onDelete?.(job.id);
  };

  const handleDownload = async () => {
    AnalyticsService.event(EventNameAMP.PROMO_ROLL_SYNTH_DOWNLOAD_CLICKED);
    const file = await FileUtils.getFileFromFileUrl(mediafileUrl, `${job.title}_${job.id}.mp3`);
    FileUtils.download(file, file.name);
  };

  return (
    <div
      className={classNames(styles.item, {
        [styles.itemPlaying]: isThisPlaying,
        [styles.itemHighlighted]: isHighlighted,
      })}>
      <Checkbox
        elementId={`job_select_${job.id}`}
        isChecked={isSelected}
        isDisabled={!isDone}
        onChange={(val) => onSelect?.(job.id, val)}
      />

      <div
        className={styles.playButton}
        role="button"
        tabIndex={0}
        onClick={handlePlay}
        onKeyDown={handlePlayKeyDown}>
        <PlayButton
          isMediafilePlaying={isThisPlaying}
          isMediaFilePaused={isPaused}
          isLoading={isQueued || isProcessing || isThisTrackLoading}
          isDisabled={!isDone}
        />
      </div>

      <div className={styles.info}>
        <PromoRollSynthJobCard job={job} />
        {isFailed && <span className={styles.error}>{failureMessage ?? TEXTS.JOB_STATUS_FAILED}</span>}
      </div>

      <div className={styles.actions}>
        {isDone && mediafileUrl && (
          <IconButton
            elementId={`job_download_${job.id}`}
            onClick={handleDownload}>
            <IconDownload />
          </IconButton>
        )}
        {isFailed && (
          <IconButton
            elementId={`job_retry_${job.id}`}
            onClick={() => onRetry?.(job)}>
            <IconRefreshCcw />
          </IconButton>
        )}
        {!isQueued && !isProcessing && (
          <IconButton
            elementId={`job_delete_${job.id}`}
            onClick={handleDelete}>
            <IconTrash />
          </IconButton>
        )}
      </div>

      {isThisPlaying && (
        <MediaListItemProgressSlider
          elementId={`job_progress_${job.id}`}
          progress={progress}
          onChange={changeProgress}
        />
      )}
    </div>
  );
};

export default PromoRollSynthJobItem;
